import { type Crusher, createCrusher } from "./crusher.js";
import { type SfxName, sfxClips } from "./sfx.js";

export { revealClipFor, type SfxName } from "./sfx.js";

interface AudioContextCtor {
  new (): AudioContext;
}

let context: AudioContext | undefined;
let master: GainNode | undefined;
let crusher: Crusher | undefined;
let unlockHandlerInstalled = false;

function getAudioContextCtor(): AudioContextCtor | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as Window & {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

function ensureContext(): AudioContext | undefined {
  if (context) {
    if (context.state === "suspended") void context.resume();
    return context;
  }
  const Ctor = getAudioContextCtor();
  if (!Ctor) return undefined;
  try {
    context = new Ctor();
    master = context.createGain();
    master.gain.value = 0.35;
    master.connect(context.destination);
    crusher = createCrusher(context, master);
  } catch {
    context = undefined;
    master = undefined;
    crusher = undefined;
    return undefined;
  }
  return context;
}

export function playSfx(name: SfxName): boolean {
  const ctx = ensureContext();
  if (!ctx || !crusher) return false;
  try {
    sfxClips[name](ctx, crusher.input, ctx.currentTime);
    return true;
  } catch {
    return false;
  }
}

/**
 * Web Audio contexts are suspended until a user gesture. Wire up
 * one-shot listeners that resume the context on first interaction so
 * the very first roll has sound.
 */
export function installAudioGestureUnlock(): void {
  if (unlockHandlerInstalled) return;
  if (typeof window === "undefined") return;
  unlockHandlerInstalled = true;
  const handler = (): void => {
    ensureContext();
  };
  window.addEventListener("pointerdown", handler, { once: true });
  window.addEventListener("keydown", handler, { once: true });
}
